import {
  couponPageSchema,
  couponPreviewSchema,
  couponRedemptionPageSchema,
  couponSchema,
  orderWithItemsSchema,
  type Coupon,
  type CouponPage,
  type CouponPreview,
  type CouponRedemptionPage,
  type CreateCouponInput,
  type OrderWithItems,
  type PreviewCouponInput,
  type UpdateCouponInput,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';

/** `/coupons` — the shop's own codes, and what one would do to a bill. */

const COUPONS = '/coupons';

export interface CouponListParams {
  usableOnly?: boolean;
  q?: string;
  page?: number;
  pageSize?: number;
}

export async function getCoupons(
  client: ApiClient,
  params: CouponListParams = {},
): Promise<CouponPage> {
  return await client.get(COUPONS, {
    schema: couponPageSchema,
    query: {
      ...(params.usableOnly ? { usableOnly: 'true' } : {}),
      ...(params.q ? { q: params.q } : {}),
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 50,
    },
  });
}

export async function createCoupon(client: ApiClient, input: CreateCouponInput): Promise<Coupon> {
  return await client.post(COUPONS, input, { schema: couponSchema });
}

export async function updateCoupon(
  client: ApiClient,
  couponId: string,
  input: UpdateCouponInput,
): Promise<Coupon> {
  return await client.patch(`${COUPONS}/${couponId}`, input, { schema: couponSchema });
}

export async function getCouponRedemptions(
  client: ApiClient,
  couponId: string,
): Promise<CouponRedemptionPage> {
  return await client.get(`${COUPONS}/${couponId}/redemptions`, {
    schema: couponRedemptionPageSchema,
  });
}

/**
 * What a code would do to this bill, without doing it.
 *
 * A POST that changes nothing — the bill is a body, not a query string — and one that answers
 * 200 with a named refusal rather than failing. A code that does not work is a sentence
 * somebody says to a customer standing at the counter, not an error the form has to interpret.
 */
export async function previewCoupon(
  client: ApiClient,
  input: PreviewCouponInput,
): Promise<CouponPreview> {
  return await client.post(`${COUPONS}/preview`, input, { schema: couponPreviewSchema });
}

/** Putting a code on a bill that already exists, and taking it off again. */
export async function applyCouponToOrder(
  client: ApiClient,
  orderId: string,
  code: string,
): Promise<OrderWithItems> {
  return await client.post(
    `/orders/${orderId}/coupon`,
    { code },
    {
      schema: orderWithItemsSchema,
    },
  );
}

export async function removeCouponFromOrder(
  client: ApiClient,
  orderId: string,
): Promise<OrderWithItems> {
  return await client.del(`/orders/${orderId}/coupon`, undefined, {
    schema: orderWithItemsSchema,
  });
}
